// Notification Service for Booking System
import { getApiUrl } from '../config/api';

const buildHostEnquiryActionUrl = (bookingId, bookingType = 'hotel') => {
  const params = new URLSearchParams({
    tab: 'enquiries',
    type: bookingType,
  });

  if (bookingId) {
    params.set('bookingId', String(bookingId));
  }

  return `/host?${params.toString()}`;
};

/**
 * Send email notification to guest after booking submission
 */
export const sendGuestBookingEmail = async (bookingData) => {
  try {
    const { guestInfo, property, checkIn, checkOut, bookingId } = bookingData;
    
    const emailData = {
      to: guestInfo.email,
      subject: 'Thank you for booking in UBSAAPP',
      template: 'guest-booking-confirmation',
      data: {
        guestName: `${guestInfo.firstName} ${guestInfo.lastName}`,
        bookingId: bookingId,
        propertyName: property.name,
        checkInDate: new Date(checkIn).toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        checkOutDate: new Date(checkOut).toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        propertyCity: property.city,
        propertyState: property.state,
        message: 'Thank you for choosing UBSAAPP. Your booking request has been sent to host. You will be notified once host approves or declines.'
      }
    };

    const response = await fetch(getApiUrl('/api/notifications/email'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(emailData)
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to send email');
    }

    console.log('✅ Guest email sent successfully');
    return { success: true, message: result.message };
  } catch (error) {
    console.error('❌ Error sending guest email:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send push notification to host about new booking request
 */
export const sendHostBookingNotification = async (bookingData) => {
  try {
    const { property, guestInfo, checkIn, checkOut, bookingId, guests } = bookingData;
    
    const notificationData = {
      hostId: property.hostId,
      type: 'new_booking_request',
      title: 'You have received a new booking request',
      message: `${guestInfo.firstName} ${guestInfo.lastName} has requested to book ${property.name}`,
      data: {
        bookingId: bookingId,
        propertyId: property._id || property.id,
        propertyName: property.name,
        guestName: `${guestInfo.firstName} ${guestInfo.lastName}`,
        guestEmail: guestInfo.email,
        guestPhone: guestInfo.phone,
        checkIn: checkIn,
        checkOut: checkOut,
        guests: guests,
        timestamp: new Date().toISOString()
      },
      priority: 'high',
      actionUrl: buildHostEnquiryActionUrl(bookingId, 'hotel')
    };

    const response = await fetch(getApiUrl('/api/notifications/push'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {}),
      },
      body: JSON.stringify(notificationData)
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to send notification');
    }

    console.log('✅ Host push notification sent successfully');
    return { success: true, message: result.message };
  } catch (error) {
    console.error('❌ Error sending host push notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send in-app notification to guest from host
 */
export const sendGuestInAppNotification = async (notificationData) => {
  try {
    const { guestId, type, title, message, bookingId, propertyName, hostName } = notificationData;
    
    const notification = {
      id: `notif_${Date.now()}`,
      guestId: guestId,
      type: type,
      title: title,
      message: message,
      bookingId: bookingId,
      propertyName: propertyName,
      hostName: hostName,
      read: false,
      createdAt: new Date().toISOString()
    };

    // Store in localStorage for demo (in production, use database)
    const guestNotificationsKey = `notifications_guest_${guestId}`;
    const existingNotifications = JSON.parse(localStorage.getItem(guestNotificationsKey) || '[]');
    existingNotifications.unshift(notification);
    localStorage.setItem(guestNotificationsKey, JSON.stringify(existingNotifications));

    // Update notification count
    const countKey = `notificationCount_guest_${guestId}`;
    const currentCount = parseInt(localStorage.getItem(countKey) || '0');
    localStorage.setItem(countKey, (currentCount + 1).toString());

    // Trigger storage event for real-time update
    window.dispatchEvent(new Event('storage'));

    console.log('✅ Guest in-app notification created:', notification);
    return { success: true, notification };
  } catch (error) {
    console.error('❌ Error creating guest in-app notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Host approves booking - send notification to guest
 */
export const sendBookingApprovalNotification = async (bookingData) => {
  try {
    const { guestId, bookingId, propertyName, hostName, checkIn, checkOut } = bookingData;
    
    // Send in-app notification
    const inAppResult = await sendGuestInAppNotification({
      guestId: guestId,
      type: 'booking_confirmed',
      title: '✅ Booking Confirmed!',
      message: `Great news! Your booking for ${propertyName} has been approved by ${hostName}. Get ready for your stay!`,
      bookingId: bookingId,
      propertyName: propertyName,
      hostName: hostName
    });

    // Send email notification to guest
    try {
      const emailResponse = await fetch(getApiUrl('/api/notifications/booking-approved-email'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          guestId: guestId,
          bookingId: bookingId,
          propertyName: propertyName,
          hostName: hostName,
          checkIn: checkIn,
          checkOut: checkOut
        })
      });

      const emailResult = await emailResponse.json();
      console.log('✅ Approval email sent to guest:', emailResult);
    } catch (emailError) {
      console.error('❌ Error sending approval email:', emailError);
    }

    return inAppResult;
  } catch (error) {
    console.error('❌ Error sending booking approval notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Host declines booking - send notification to guest
 */
export const sendBookingDeclineNotification = async (bookingData) => {
  try {
    const { guestId, bookingId, propertyName, hostName } = bookingData;
    
    // Send in-app notification
    const inAppResult = await sendGuestInAppNotification({
      guestId: guestId,
      type: 'booking_declined',
      title: '❌ Booking Declined',
      message: `Unfortunately, your booking request for ${propertyName} was declined by ${hostName}. Please try another property.`,
      bookingId: bookingId,
      propertyName: propertyName,
      hostName: hostName
    });

    // Send email notification to guest
    try {
      const emailResponse = await fetch(getApiUrl('/api/notifications/booking-declined-email'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          guestId: guestId,
          bookingId: bookingId,
          propertyName: propertyName,
          hostName: hostName
        })
      });

      const emailResult = await emailResponse.json();
      console.log('✅ Decline email sent to guest:', emailResult);
    } catch (emailError) {
      console.error('❌ Error sending decline email:', emailError);
    }

    return inAppResult;
  } catch (error) {
    console.error('❌ Error sending booking decline notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Host sends message to guest
 */
export const sendHostMessageNotification = async (messageData) => {
  try {
    const { guestId, bookingId, propertyName, hostName, message } = messageData;
    
    return await sendGuestInAppNotification({
      guestId: guestId,
      type: 'host_message',
      title: '💬 Message from Host',
      message: `${hostName} sent you a message: "${message}"`,
      bookingId: bookingId,
      propertyName: propertyName,
      hostName: hostName
    });
  } catch (error) {
    console.error('❌ Error sending host message notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send in-app notification to host (MongoDB only)
 */
export const sendHostInAppNotification = async (bookingData) => {
  try {
    const { property, guestInfo, bookingId, checkIn, checkOut, guests } = bookingData;
    
    // Ensure we have a valid hostId and convert to string
    let hostId = property.hostId;
    
    // Convert ObjectId to string if needed
    if (hostId && typeof hostId === 'object') {
      hostId = hostId.toString();
    }
    
    if (!hostId) {
      console.error('❌ No hostId found in property:', property);
      throw new Error('Property hostId is required for notification');
    }
    
    console.log('✅ Creating notification for hostId:', hostId);
    
    // Save to database via API
    const response = await fetch(getApiUrl('/api/notifications/in-app'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: hostId,
        userType: 'host',
        type: 'booking_request',
        title: 'New Booking Request',
        message: `${guestInfo.firstName} ${guestInfo.lastName} wants to book ${property.name} from ${new Date(checkIn).toLocaleDateString()} to ${new Date(checkOut).toLocaleDateString()}`,
        bookingId: bookingId,
        propertyId: property._id || property.id,
        propertyName: property.name,
        guestName: `${guestInfo.firstName} ${guestInfo.lastName}`,
        guestEmail: guestInfo.email,
        metadata: {
          checkIn: checkIn,
          checkOut: checkOut,
          guests: guests
        },
        actionRequired: true,
        actionUrl: buildHostEnquiryActionUrl(bookingId, 'hotel')
      })
    });

    const result = await response.json();

    if (result.success) {
      console.log('✅ Notification saved to MongoDB:', result.notification._id);
      return { success: true, notification: result.notification };
    } else {
      console.error('❌ Failed to save notification:', result.message);
      throw new Error(result.message);
    }
  } catch (error) {
    console.error('❌ Error creating in-app notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send confirmation notification to host when they approve/decline a booking (MongoDB only)
 */
export const sendHostActionConfirmationNotification = async (actionData) => {
  try {
    let { hostId, action, bookingId, propertyName, guestName, checkIn, checkOut } = actionData;
    
    // Convert ObjectId to string if needed
    if (hostId && typeof hostId === 'object') {
      hostId = hostId.toString();
    }
    
    const response = await fetch(getApiUrl('/api/notifications/in-app'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: hostId,
        userType: 'host',
        type: action === 'approved' ? 'booking_confirmed' : 'booking_declined',
        title: action === 'approved' ? '✅ Booking Approved' : '❌ Booking Declined',
        message: action === 'approved' 
          ? `You have successfully approved the booking for ${propertyName} by ${guestName}. The guest has been notified.`
          : `You have declined the booking request for ${propertyName} by ${guestName}.`,
        bookingId: bookingId,
        propertyName: propertyName,
        metadata: {
          guestName: guestName,
          checkIn: checkIn,
          checkOut: checkOut,
          action: action
        },
        actionRequired: false
      })
    });

    const result = await response.json();

    if (result.success) {
      console.log('✅ Host action confirmation notification saved to MongoDB');
      return { success: true, notification: result.notification };
    } else {
      console.error('❌ Failed to save notification:', result.message);
      throw new Error(result.message);
    }
  } catch (error) {
    console.error('❌ Error creating host action confirmation notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send email to host about new booking request
 */
export const sendHostBookingEmail = async (bookingData) => {
  try {
    const { property, guestInfo, checkIn, checkOut, bookingId, guests, totalPrice } = bookingData;
    
    // Get host email from multiple possible sources
    const hostEmail = property.hostEmail || 
                     property.hostId?.email || 
                     property.host?.email;
    
    console.log('📧 Host email resolved to:', hostEmail);
    console.log('📦 Property data:', {
      hostEmail: property.hostEmail,
      hostIdEmail: property.hostId?.email,
      hostObjEmail: property.host?.email
    });
    
    if (!hostEmail) {
      console.warn('⚠️ Host email not available for booking notification. Cannot send email to host.');
      return {
        success: false,
        message: 'Host email not available. Please contact support.',
      };
    }
    
    const emailData = {
      to: hostEmail,
      subject: 'New Booking Request - Action Required',
      template: 'host-booking-request',
      data: {
        hostName: property.hostName || property.hostId?.name || property.host?.name || 'Host',
        bookingId: bookingId,
        propertyName: property.name,
        guestName: `${guestInfo.firstName} ${guestInfo.lastName}`,
        guestEmail: guestInfo.email,
        guestPhone: guestInfo.phone || 'Not provided',
        checkInDate: new Date(checkIn).toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        checkOutDate: new Date(checkOut).toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        guests: guests,
        totalAmount: totalPrice,
        approveUrl: `${window.location.origin}/host/bookings/${bookingId}/approve`,
        declineUrl: `${window.location.origin}/host/bookings/${bookingId}/decline`,
        message: 'You have received a new booking request. Please review and respond within 24 hours.'
      }
    };

    const response = await fetch(getApiUrl('/api/notifications/email'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(emailData)
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to send email');
    }

    console.log('✅ Host email sent successfully');
    return { success: true, message: result.message };
  } catch (error) {
    console.error('❌ Error sending host email:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Save booking to localStorage and database
 */
export const saveBooking = async (bookingData) => {
  try {
    const booking = {
      ...bookingData,
      status: 'pending',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Save to localStorage for demo
    const userBookingsKey = `bookings_user_${bookingData.guestInfo.userId || 'guest'}`;
    const existingBookings = JSON.parse(localStorage.getItem(userBookingsKey) || '[]');
    existingBookings.unshift(booking);
    localStorage.setItem(userBookingsKey, JSON.stringify(existingBookings));

    // Also save to general bookings
    const allBookings = JSON.parse(localStorage.getItem('userBookings') || '[]');
    allBookings.unshift(booking);
    localStorage.setItem('userBookings', JSON.stringify(allBookings));

    // In production, save to database
    const response = await fetch(getApiUrl('/api/bookings'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(booking)
    });

    if (!response.ok) {
      throw new Error('Failed to save booking');
    }

    console.log('✅ Booking saved successfully:', booking);
    return { success: true, booking };
  } catch (error) {
    console.error('❌ Error saving booking:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send all notifications after booking submission
 * Note: Booking should already be saved to database before calling this function
 */
export const sendBookingNotifications = async (bookingData) => {
  console.log('📧 Starting notification process...');
  
  const results = {
    guestEmail: null,
    hostPushNotification: null,
    hostInAppNotification: null,
    hostEmail: null
  };

  try {
    // 1. Send email to guest
    console.log('📧 Sending email to guest...');
    results.guestEmail = await sendGuestBookingEmail(bookingData);

    // 2. Send push notification to host
    console.log('🔔 Sending push notification to host...');
    results.hostPushNotification = await sendHostBookingNotification(bookingData);

    // 3. Send in-app notification to host
    console.log('📱 Creating in-app notification for host...');
    results.hostInAppNotification = await sendHostInAppNotification(bookingData);

    // Note: Host email is already sent by the server when booking is created (POST /api/bookings/stay)
    // - Do NOT call sendHostBookingEmail here to avoid duplicate emails

    console.log('✅ All notifications sent successfully!');
    return {
      success: true,
      results
    };
  } catch (error) {
    console.error('❌ Error in notification process:', error);
    return {
      success: false,
      error: error.message,
      results
    };
  }
};

/**
 * Format email template for guest
 */
export const getGuestEmailTemplate = (data) => {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: #003580; color: white; padding: 20px; text-align: center; }
        .content { background: #f9f9f9; padding: 30px; }
        .booking-details { background: white; padding: 20px; margin: 20px 0; border-radius: 8px; }
        .detail-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee; }
        .footer { text-align: center; padding: 20px; color: #666; font-size: 12px; }
        .button { background: #003580; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; display: inline-block; margin: 20px 0; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>Thank you for booking in UBSAAPP</h1>
        </div>
        <div class="content">
          <h2>Hello ${data.guestName},</h2>
          <p>Thank you for choosing UBSAAPP. Your booking request has been sent to the host.</p>
          
          <div class="booking-details">
            <h3>Booking Details</h3>
            <div class="detail-row">
              <strong>Booking ID:</strong>
              <span>${data.bookingId}</span>
            </div>
            <div class="detail-row">
              <strong>Property:</strong>
              <span>${data.propertyName}</span>
            </div>
            <div class="detail-row">
              <strong>Check-in:</strong>
              <span>${data.checkInDate}</span>
            </div>
            <div class="detail-row">
              <strong>Check-out:</strong>
              <span>${data.checkOutDate}</span>
            </div>
          </div>
          
          <p><strong>What's next?</strong></p>
          <ul>
            <li>The host will review your booking request</li>
            <li>You will be notified once the host approves or declines</li>
            <li>This usually takes less than 24 hours</li>
          </ul>
          
          <a href="${window.location.origin}/trips" class="button">View My Bookings</a>
        </div>
        <div class="footer">
          <p>© 2024 UBSAAPP. All rights reserved.</p>
          <p>Need help? Contact us at support@ubsaapp.com</p>
        </div>
      </div>
    </body>
    </html>
  `;
};

export default {
  sendGuestBookingEmail,
  sendHostBookingNotification,
  sendHostInAppNotification,
  sendHostBookingEmail,
  sendBookingNotifications,
  getGuestEmailTemplate,
  sendGuestInAppNotification,
  sendBookingApprovalNotification,
  sendBookingDeclineNotification,
  sendHostMessageNotification,
  sendHostActionConfirmationNotification
};
